import { supabase } from '../lib/supabase';

export interface AuditLogData {
  actorType: 'admin' | 'investor';
  actorId?: string;
  actorName: string;
  actionType: 'create' | 'update' | 'delete' | 'read';
  entityType: 'investment' | 'profit' | 'report' | 'period' | 'investor' | 'message' | 'profile';
  entityId?: string;
  details: string;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, any>;
}

// ✅ دالة للحصول على عنوان IP (محاكاة - في الإنتاج استخدم API خارجي)
async function getClientIP(): Promise<string | null> {
  try {
    // في بيئة الإنتاج، استخدم خدمة مثل ipapi.co
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: AbortSignal.timeout(3000)
    });
    const data = await response.json();
    return data.ip || null;
  } catch {
    return null;
  }
}

/**
 * ✅ دالة محسّنة لتسجيل العمليات في جدول audit_logs
 */
export const logAuditEvent = async (data: AuditLogData): Promise<void> => {
  try {
    // الحصول على معلومات إضافية
    const ipAddress = data.ipAddress || await getClientIP();
    const userAgent = data.userAgent || navigator.userAgent;

    // التحقق من صحة البيانات
    if (!data.actorName || !data.actionType || !data.entityType) {
      console.error('❌ بيانات غير كاملة لتسجيل العملية');
      return;
    }

    const logEntry = {
      actor_type: data.actorType,
      actor_id: data.actorId || null,
      actor_name: data.actorName,
      action_type: data.actionType,
      entity_type: data.entityType,
      entity_id: data.entityId || null,
      details: data.details,
      ip_address: ipAddress,
      user_agent: userAgent,
      metadata: data.metadata || null,
      created_at: new Date().toISOString(),
    };

    const { error } = await supabase.from('audit_logs').insert([logEntry]);

    if (error) {
      console.error('❌ خطأ في تسجيل العملية:', error);
    } else {
      console.log('✅ تم تسجيل العملية بنجاح:', data.details);
    }
  } catch (error) {
    console.error('❌ خطأ غير متوقع في تسجيل العملية:', error);
  }
};

/**
 * ✅ دالة مساعدة للحصول على معلومات المتصفح
 */
export const getBrowserInfo = () => {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
  };
};

/**
 * ✅ دالة لتسجيل محاولات تسجيل الدخول الفاشلة
 */
export const logFailedLoginAttempt = async (
  actorType: 'admin' | 'investor',
  username: string,
  reason: string
): Promise<void> => {
  await logAuditEvent({
    actorType,
    actorName: username,
    actionType: 'read',
    entityType: 'profile',
    details: `محاولة تسجيل دخول فاشلة: ${reason}`,
    metadata: {
      failed: true,
      reason,
      timestamp: new Date().toISOString(),
    },
  });
};

/**
 * ✅ دالة لتسجيل التغييرات الحساسة
 */
export const logSensitiveChange = async (
  actorType: 'admin' | 'investor',
  actorId: string,
  actorName: string,
  entityType: AuditLogData['entityType'],
  entityId: string,
  changes: Record<string, any>
): Promise<void> => {
  await logAuditEvent({
    actorType,
    actorId,
    actorName,
    actionType: 'update',
    entityType,
    entityId,
    details: `تعديل بيانات حساسة في ${entityType}`,
    metadata: {
      changes,
      sensitive: true,
      timestamp: new Date().toISOString(),
    },
  });
};
